import { t } from "@lingui/macro";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { RelayEnvironmentProvider, useRelayEnvironment } from "react-relay";

import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { Button } from "@whoosh/design-system/src/components/controls";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box, HStack } from "@whoosh/design-system/src/components/primitive";

import { DiningAgendaFilters } from "../../../AgendaScreen/AgendaScreens/DiningAgendaScreen/components/DiningAgendaFilters";
import type { DiningAgendaFilterState } from "../../../AgendaScreen/AgendaScreens/DiningAgendaScreen/components/DiningAgendaFilters";
import {
  selectedFacilityId_atom,
  selectedPartySize_atom,
  selectedSeatingShift_atom,
} from "../atoms";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  date: string;
};

export const DiningAgendaFiltersModal = ({ date, isOpen, onClose }: Props) => {
  const relayEnvironment = useRelayEnvironment();
  const [selectedFacilityId, setSelectedFacilityId] = useAtom(
    selectedFacilityId_atom
  );
  const [selectedPartySize, setSelectedPartySize] = useAtom(
    selectedPartySize_atom
  );
  const [selectedSeatingShift, setSelectedSeatingShift] = useAtom(
    selectedSeatingShift_atom
  );

  const [filterState, setFilterState] = useState<DiningAgendaFilterState>({
    selectedFacilityId,
    selectedPartySize,
    selectedSeatingShift,
  });

  useEffect(() => {
    if (isOpen) {
      setFilterState({
        selectedFacilityId,
        selectedPartySize,
        selectedSeatingShift,
      });
    }
  }, [isOpen, selectedFacilityId, selectedPartySize, selectedSeatingShift]);

  const handleSave = () => {
    setSelectedFacilityId(filterState.selectedFacilityId);
    setSelectedPartySize(filterState.selectedPartySize);
    setSelectedSeatingShift(filterState.selectedSeatingShift);
    onClose();
  };

  const handleReset = () => {
    setFilterState({
      selectedFacilityId: null,
      selectedPartySize: 4,
      selectedSeatingShift: null,
    });
  };

  return (
    <StandardModal
      upperTitle={t`Filters`}
      isOpen={isOpen}
      onClose={onClose}
      hideFooterPadding
      content={
        <RelayEnvironmentProvider environment={relayEnvironment}>
          <Box px={6}>
            <DiningAgendaFilters
              date={date}
              filterState={filterState}
              isMobile={true}
              onFilterStateChange={setFilterState}
            />
          </Box>
        </RelayEnvironmentProvider>
      }
      footerContent={
        <ScreenFooter>
          <HStack flex={1} justifyContent="space-between">
            <Box alignSelf="flex-start">
              <Button
                onPress={handleReset}
                variant="ghost"
                accessibilityLabel={t`Reset button`}
                testID="dining-filters-reset-button"
              >{t`Reset`}</Button>
            </Box>
            <Box alignSelf="flex-end">
              <Button
                onPress={handleSave}
                accessibilityLabel={t`Save and Close button`}
                testID="dining-filters-save-and-close-button"
              >{t`Save and Close`}</Button>
            </Box>
          </HStack>
        </ScreenFooter>
      }
    />
  );
};
